import React, { createContext, useState, useContext, useCallback, ReactNode, ComponentType } from 'react';

interface DialogState {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface ShowConfirmationOptions {
  title?: string;
  message?: string;
  onConfirm: () => void;
  onCancel: () => void;
}
 
interface CustomConfirmDialogProps {
    open: boolean;                    
    title: string;                    
    message: string;                  
    onConfirm: () => void;            
    onCancel: () => void;             
    onClose: () => void;              
    confirmLabel?: string;           
    cancelLabel?: string;            
}

interface ConfirmationDialogProviderProps {
  children: ReactNode;
  CustomDialogComponent: ComponentType<CustomConfirmDialogProps>;
}

interface ConfirmationDialogContextType {
  showConfirmCloseDialog: (options: ShowConfirmationOptions) => void;
  //closeConfirmation: () => void;
}  

const ConfirmCloseDialogContext = createContext<ConfirmationDialogContextType | undefined>(undefined);

export const useConfirmCloseDialog = (): ConfirmationDialogContextType => {
  const context = useContext(ConfirmCloseDialogContext);
  if (context === undefined) {
    throw new Error('useConfirmationDialog must be used within a ConfirmationDialogProvider');
  }
  return context;
};

export const ConfirmCloseDialogProvider: React.FC<ConfirmationDialogProviderProps> = ({ children,CustomDialogComponent }) => {
  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {}, // Callback function for when the user confirms
    onCancel: () => {},  // Callback function for when the user cancels
  });

  const showConfirmCloseDialog = useCallback(({ title, message, onConfirm, onCancel }: ShowConfirmationOptions) => {
    setDialogState({
      open: true,
      title: title || 'Discard Changes?', 
      message: message || 'You have unsaved changes. Are you sure you want to close and discard them?',  
      onConfirm: onConfirm, 
      onCancel: onCancel,    
    });
  }, []);

  const closeConfirmation = useCallback(() => {
    setDialogState(prevState => ({ ...prevState, open: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    dialogState.onConfirm();  
    closeConfirmation();      
  }, [dialogState, closeConfirmation]);  

  const handleCancel = useCallback(() => {
    dialogState.onCancel();  
    closeConfirmation();     
  }, [dialogState, closeConfirmation]);  

  const contextValue = React.useMemo(() => ({showConfirmCloseDialog: showConfirmCloseDialog }), [showConfirmCloseDialog]);

  return (
    <ConfirmCloseDialogContext.Provider value={contextValue}>
      {children}
       <CustomDialogComponent
        open={dialogState.open}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        message={dialogState.message}
        title={dialogState.title}
        onCancel={handleCancel}
        confirmLabel="Yes"
        cancelLabel="No"
      ></CustomDialogComponent> 
    </ConfirmCloseDialogContext.Provider>
  );
};
